/**
 * Compact thumbnail strip for the Evidence sub-agent child inline display:
 * up to eight thumbnails plus a "+N" tile that expands the rest. Purely
 * presentational — callers aggregate/dedup (`collectEvidenceImages`) and
 * flag-gate. Images are untrusted third-party content: URLs are https-only
 * (enforced at parse), rendered with `no-referrer` + lazy loading, and a
 * thumbnail that fails to load is dropped from the strip.
 */

import type { EvidenceAgentImage } from '@heidi/agents/contracts';
import { focusRing } from '@heidi/design-system/styles';
import { typographyVariant } from '@heidi/design-system/typography';
import type { EvidenceImage } from '@heidi/evidence/contracts';
import { cn } from '@heidi/utils';
import { useCallback, useMemo, useState } from 'react';

import { EvidenceLightboxPortal } from './EvidenceLightboxPortal';

const VISIBLE_COUNT = 8;

const thumbnailTileClasses = cn(
  'h-16 w-24 shrink-0 overflow-hidden rounded-md border border-border',
  focusRing
);

export interface EvidenceImageStripProps {
  images: EvidenceAgentImage[];
  className?: string;
}

export const EvidenceImageStrip = ({
  images,
  className,
}: EvidenceImageStripProps): JSX.Element | null => {
  const [expanded, setExpanded] = useState(false);
  const [failedUrls, setFailedUrls] = useState<ReadonlySet<string>>(new Set());
  const [lightboxImage, setLightboxImage] = useState<EvidenceImage | null>(null);

  const closeLightbox = useCallback(() => setLightboxImage(null), []);

  const usable = useMemo(
    () =>
      images.filter(
        (image): image is EvidenceAgentImage & { url: string } =>
          typeof image.url === 'string' &&
          image.url.startsWith('https://') &&
          !failedUrls.has(image.url)
      ),
    [images, failedUrls]
  );
  if (usable.length === 0) return null;

  const visible = expanded ? usable : usable.slice(0, VISIBLE_COUNT);
  const hiddenCount = usable.length - visible.length;

  return (
    <>
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        {visible.map((image, index) => {
          const label = image.title ?? image.description;
          return (
            <button
              key={image.url}
              type="button"
              className={cn(thumbnailTileClasses, 'group bg-surface p-0')}
              onClick={() =>
                setLightboxImage({
                  n: index + 1,
                  url: image.url,
                  description: image.description ?? image.title,
                })
              }
              aria-label={label ? `View image: ${label}` : `View image ${index + 1}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={image.url}
                alt={label ?? ''}
                className="size-full object-cover transition-opacity group-hover:opacity-80"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={() =>
                  setFailedUrls((previous) => new Set(previous).add(image.url))
                }
              />
            </button>
          );
        })}
        {hiddenCount > 0 && (
          <button
            type="button"
            className={cn(
              thumbnailTileClasses,
              'bg-surface text-text-secondary transition-colors hover:bg-surface-hover',
              typographyVariant({ variant: 'captionBold' })
            )}
            onClick={() => setExpanded(true)}
            aria-label={`Show ${hiddenCount} more images`}
          >
            +{hiddenCount}
          </button>
        )}
      </div>
      <EvidenceLightboxPortal image={lightboxImage} onClose={closeLightbox} />
    </>
  );
};
